'use client';

import { Popover, TextInput } from 'flowbite-react';
import { type FunctionComponent, useEffect } from 'react';

import { AbbCalendar } from '@/assets/icons/components/abb-calendar';
import { AbbCaretDown } from '@/assets/icons/components/abb-caret-down';
import { AbbClose } from '@/assets/icons/components/abb-close';
import { useTheme } from '@/modules/utils/custom-hooks/use-theme';

import { Datepicker } from '../datepicker/datepicker';
import { sizes, sizeVariants } from '../utils';
import { useDropdown } from './hooks/use-dropdown';
import type { SearchableDropdownItemProps, SearchableDropdownProps } from './types';

const customTheme = {
  base: 'flex w-full',
  field: {
    icon: {
      base: 'pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3',
      svg: 'h-5 w-5 text-dark'
    },
    input: {
      base: 'block w-full cursor-pointer disabled:cursor-not-allowed disabled:opacity-100 placeholder-n400_input_stroker dark:!placeholder-n700_text_dark_mode',
      sizes: {
        sm: 'p-1.5 text-sm',
        md: 'p-2 text-base',
        lg: 'p-3 text-lg'
      },
      withAddon: {
        on: 'w-full',
        off: 'w-full rounded-md border border-n200_background pl-2 focus:!ring-n400_input_stroker focus:border-n400_input_stroker'
      }
    }
  }
};

const popoverCustomTheme = {
  base: 'absolute z-20 inline-block w-max h-fit bg-white outline-none rounded-lg shadow-lg dark:bg-bg_dark',
  content: 'overflow-hidden rounded-md'
};

const iconSizes = {
  large: 'h-7 w-7',
  medium: 'h-6 w-6',
  small: 'h-5 w-5'
};

const inputBackgrounds = {
  light: '!bg-white dark:!bg-bg_dark',
  dark: '!bg-n50_hover dark:!bg-bg_dark'
};

export const SearchableDropdown: FunctionComponent<SearchableDropdownProps> = ({
  options,
  onSelect,
  className,
  sizing = 'medium',
  placeholder,
  hasDatePicker = false,
  calendarDirection = 'past',
  fixedStartDate,
  inputBackground = 'dark',
  onClear,
  bottomButton = null,
  disabled = false,
  externalSearchTerm,
  isSearchable = true,
  error = ''
}) => {
  const { t, format, dropdownRef, isAbove, isOpen, searchTerm, userTyped, setUserTyped, setIsOpen, setSearchTerm } = useDropdown();
  const { isDarkTheme } = useTheme();

  const filteredOptions =
    !userTyped && isOpen ? options : options.filter((opt) => opt.label.toLowerCase().includes(searchTerm.toLowerCase()));

  const handleSelect = (option: SearchableDropdownItemProps) => {
    if (typeof option.value !== 'string' && hasDatePicker) {
      setSearchTerm(`${format(option.value.from, 'DD/MM/YYYY')} - ${format(option.value.to, 'DD/MM/YYYY')}`);
      onSelect([option.value.from, option.value.to ? option.value.to : '']);
    } else if (typeof option.value === 'string') {
      setSearchTerm(option.label);
      onSelect(option.value);
    }

    if (!option.range) {
      setIsOpen(false);
    }
  };

  const clearInput = () => {
    setSearchTerm('');
    setIsOpen(false);
    onClear?.();
  };

  const iconClasses = `absolute right-2 text-dark cursor-pointer`;
  const minDate = calendarDirection === 'past' ? new Date(0) : new Date();
  const maxDate = calendarDirection === 'past' ? new Date() : new Date(new Date().setFullYear(new Date().getFullYear() + 1));

  useEffect(() => {
    if (externalSearchTerm) setSearchTerm(externalSearchTerm);
  }, [externalSearchTerm]);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className='flex items-center rounded-full w-full'>
        <TextInput
          theme={{
            ...customTheme,
            field: {
              input: {
                ...customTheme.field.input,
                base: `${customTheme.field.input.base} ${inputBackgrounds[inputBackground]} disabled:${hasDatePicker && !searchTerm ? 'cursor-default' : 'cursor-not-allowed'}`
              }
            }
          }}
          readOnly={hasDatePicker || !isSearchable}
          disabled={(hasDatePicker && searchTerm !== '') || disabled}
          type='text'
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setUserTyped(true);
            if (e.target.value === '') {
              onClear?.();
            }
          }}
          onClick={() => setIsOpen(!isOpen)}
          placeholder={placeholder ?? t('T_SELECT_OPTION')}
          sizing={sizeVariants[sizing]}
        />
        {searchTerm ? (
          <div className={`${iconClasses} flex items-center`}>
            <AbbClose fill={isDarkTheme ? '#f1f1f1' : '#1f1f1f'} className={`${iconSizes[sizing]} mt-0.5`} onClick={clearInput} />
            <div className='w-px h-6 bg-gray-300' />
            <AbbCaretDown
              fill={isDarkTheme ? '#f1f1f1' : '#1f1f1f'}
              onClick={disabled ? undefined : () => setIsOpen(!isOpen)}
              className={`${iconSizes[sizing]} transition-transform duration-200 ${isOpen ? 'rotate-180' : 'rotate-0'}`}
            />
          </div>
        ) : (
          <AbbCaretDown
            fill={isDarkTheme ? '#f1f1f1' : '#1f1f1f'}
            onClick={disabled ? undefined : () => setIsOpen(!isOpen)}
            className={`${iconSizes[sizing]} absolute right-2 text-dark cursor-pointer transition-transform duration-200 ${isOpen ? 'rotate-180' : 'rotate-0'}`}
          />
        )}
      </div>
      {error && <p className='!text-brand font-medium text-xs mt-1'>{error}</p>}
      {isOpen && (
        <Popover
          theme={popoverCustomTheme}
          arrow={false}
          placement='right-start'
          content={
            hasDatePicker && (
              <Datepicker
                showPreviousMonths={false}
                setIsOpen={setIsOpen}
                onSelect={onSelect}
                setSearchTerm={setSearchTerm}
                minDate={minDate}
                maxDate={maxDate}
                fixedStartDate={fixedStartDate}
              />
            )
          }
          key={'popover'}
        >
          <div
            className={`absolute z-10 w-full right-0 left-0 bg-white rounded-md shadow-dropdown max-h-80 overflow-hidden dark:bg-bg_dark dark:text-n700_text_dark_mode ${isAbove ? 'mb-0.5 bottom-full' : 'mt-0.5 top-full'}`}
          >
            <ul className='max-h-60 overflow-y-auto scrollbar-rounded'>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) =>
                  option.range && hasDatePicker ? (
                    <li
                      key={option.label}
                      className={`cursor-pointer p-2 hover:bg-n50_hover hover:text-dark dark:hover:bg-n600_text dark:hover:text-n700_text_dark_mode ${sizes[sizing]}`}
                    >
                      <div className='flex'>
                        <div className='w-6 h-6 mr-2'>
                          <AbbCalendar size={24} fill={isDarkTheme ? '#f1f1f1' : '#1f1f1f'} />
                        </div>
                        {option.label}
                      </div>
                    </li>
                  ) : (
                    <li
                      key={option.label}
                      onClick={() => {
                        handleSelect(option);
                        setUserTyped(false);
                      }}
                      className={`cursor-pointer p-2 hover:bg-n50_hover hover:text-dark dark:hover:bg-n600_text dark:hover:text-n700_text_dark_mode ${sizes[sizing]}`}
                    >
                      {option.label}
                    </li>
                  )
                )
              ) : (
                <div className='px-4 py-2 text-gray-500 dark:text-gray-300'>{t('T_NO_OPTIONS')}</div>
              )}
            </ul>
            {bottomButton}
          </div>
        </Popover>
      )}
    </div>
  );
};
